import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { BaseApiService } from './base-api.service';
import {
  AppointmentResumoResponse,
  AppointmentDiaResponse,
  AppointmentSemanaResponse,
  AppointmentMesResponse
} from '../interfaces/appointment.interface';

@Injectable({
  providedIn: 'root'
})
export class AppointmentsService extends BaseApiService {

  /**
   * Obtém o resumo de agendamentos para um DN específico
   */
  getAppointmentsResumo(cd_dn: string): Observable<AppointmentResumoResponse> {
    const endpoint = `/lead/agendamentos/resumo?dn=${cd_dn}`;
    return this.get<AppointmentResumoResponse>(endpoint).pipe(
      catchError((error) => {
        console.error('AppointmentsService - Error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtém agendamentos do dia
   */
  getAppointmentsDia(cd_dn: string, data: string = this.getTodayISO()): Observable<AppointmentDiaResponse> {
    const endpoint = `/lead/agendamentos/dia?cd_dn=${cd_dn}&data=${data}`;
    return this.get<AppointmentDiaResponse>(endpoint).pipe(
      catchError((error) => {
        console.error('AppointmentsService - Error getAppointmentsDia:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtém agendamentos da semana
   */
  getAppointmentsSemana(cd_dn: string, data: string = this.getTodayISO()): Observable<AppointmentSemanaResponse> {
    const endpoint = `/lead/agendamentos/semana?cd_dn=${cd_dn}&dataReferencia=${data}`;
    return this.get<AppointmentSemanaResponse>(endpoint).pipe(
      catchError((error) => {
        console.error('AppointmentsService - Error getAppointmentsSemana:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtém agendamentos do mês
   */
  getAppointmentsMes(
    cd_dn: string = '1080',
    mes?: number,
    ano?: number
  ): Observable<AppointmentMesResponse> {
    // Se mês não for informado, usa o mês atual
    const mesAtual = mes !== undefined ? mes : (new Date().getMonth() + 1);

    // Se ano não for informado, usa o ano atual
    const anoAtual = ano !== undefined ? ano : new Date().getFullYear();

    const endpoint = `/lead/agendamentos/mes?cd_dn=${cd_dn}&mes=${mesAtual}&ano=${anoAtual}`;

    return this.get<AppointmentMesResponse>(endpoint).pipe(
      catchError((error) => {
        console.error('AppointmentsService - Error getAppointmentsMes:', error);
        return throwError(() => error);
      })
    );
  }
  /**
   * Obtém agendamentos do mês atual (método conveniente)
   */
  getAppointmentsMesAtual(cd_dn: string = '1080'): Observable<AppointmentMesResponse> {
    return this.getAppointmentsMes(cd_dn);
  }

  /**
   * Obtém a data atual no formato ISO 8601 (YYYY-MM-DD)
   */
  getTodayISO(): string {
    return this.formatDateToISO(new Date());
  }

  /**
   * Formata uma data para ISO 8601 (YYYY-MM-DD)
   */
  formatDateToISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Formata hora do formato ISO para HH:MM
   */
  formatTimeFromISO(isoString: string): string {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(':', 'h');
    } catch (error) {
      return isoString;
    }
  }

  /**
   * Extrai apenas a hora de um ISO string (HH:MM)
   */
  extractHourFromISO(isoString: string): string {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return isoString;
    }
  }

  /**
   * Ordena agendamentos por hora
   */
  sortAppointmentsByTime(appointments: any[]): any[] {
    return appointments.sort((a, b) => {
      const timeA = this.extractHourFromISO(a.horaAgendamento || a.dtAgendamento || '1970-01-01T00:00:00');
      const timeB = this.extractHourFromISO(b.horaAgendamento || b.dtAgendamento || '1970-01-01T00:00:00');
      return timeA.localeCompare(timeB);
    });
  }
}
